import type { PricingType } from '@/theme/uboTheme';
import type { InvestmentRow } from '@/types';

/**
 * Onthoudt per tabel hoe de rijen eruitzagen in een prijsmodel.
 *
 * Bij een wissel naar "vast bedrag" verdwijnen de uren- en tariefkolommen uit het
 * document — die kolommen bestaan daar simpelweg niet. Zonder geheugen zijn uren en
 * uurtarief dus weg zodra je terugwisselt. Deze cache bewaart de laatste stand per
 * prijsmodel, zodat terugwisselen de oude getallen teruggeeft in plaats van € 0,00.
 *
 * De cache leeft alleen in het geheugen van deze sessie: na een herlaadactie kan er
 * niets meer worden teruggehaald wat niet in het document staat.
 */
const snapshots = new Map<string, Partial<Record<PricingType, InvestmentRow[]>>>();

export function rememberPricingRows(
  tableId: string,
  pricingType: PricingType,
  rows: InvestmentRow[]
): void {
  if (!tableId) return;
  const forTable = snapshots.get(tableId) ?? {};
  forTable[pricingType] = rows.map((row) => ({ ...row }));
  snapshots.set(tableId, forTable);
}

export function readPricingRows(
  tableId: string,
  pricingType: PricingType
): InvestmentRow[] | undefined {
  if (!tableId) return undefined;
  return snapshots.get(tableId)?.[pricingType];
}

export function forgetPricingRows(tableId: string): void {
  snapshots.delete(tableId);
}

function hasNumber(value: number | undefined): boolean {
  return typeof value === 'number' && value !== 0;
}

/**
 * Vult de kolommen van het doelprijsmodel aan vanuit de onthouden stand.
 *
 * De live rijen zijn leidend: omschrijvingen en waarden die nú in het document
 * staan blijven staan. Alleen wat het doelmodel nodig heeft én in de live rij
 * ontbreekt (uren en tarief na een tussenstap via "vast bedrag") komt uit de
 * snapshot, gekoppeld op rijvolgorde.
 */
export function mergePricingRows(
  target: PricingType,
  liveRows: InvestmentRow[],
  rememberedRows: InvestmentRow[] | undefined
): InvestmentRow[] {
  if (!rememberedRows?.length) return liveRows;

  return liveRows.map((row, index) => {
    const remembered = rememberedRows[index];
    if (!remembered) return row;

    if (target === 'hourly') {
      const price = hasNumber(row.price) ? row.price : remembered.price;
      const hours = hasNumber(row.hours) ? row.hours : remembered.hours;
      const merged: InvestmentRow = { ...row };
      if (price != null) merged.price = price;
      if (hours != null) merged.hours = hours;
      if (!hasNumber(row.subtotal) && price != null && hours != null) {
        merged.subtotal = price * hours;
      }
      if (!row.useText && remembered.useText && !hasNumber(price)) {
        merged.useText = true;
        merged.text = remembered.text;
      }
      return merged;
    }

    if (target === 'recurring') {
      if (hasNumber(row.subtotal) || row.useText) return row;
      const merged: InvestmentRow = { ...row };
      if (hasNumber(remembered.subtotal)) {
        merged.subtotal = remembered.subtotal;
      } else if (hasNumber(remembered.price) && hasNumber(remembered.hours)) {
        merged.subtotal = (remembered.price ?? 0) * (remembered.hours ?? 0);
      } else if (remembered.useText) {
        merged.useText = true;
        merged.text = remembered.text;
      }
      return merged;
    }

    return row;
  });
}
